import { AlertTriangle, Check, Clock, FileText, Upload, X } from 'lucide-react';
import { type LucideIcon } from 'lucide-react';

import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { DocValidadeBadge } from '@/modulos/operacao/comum/DocValidadeBadge';
import { cn } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Progress } from '@/ui/base/progress';

export type SituacaoDoChecklist = 'aprovado' | 'enviado' | 'pendente' | 'rejeitado';

export interface ItemDoChecklist {
  id: string;
  nomeDocumento: string;
  obrigatorio: boolean;
  status: SituacaoDoChecklist;
  descricao?: string;
  documento?: { arquivoNome: string; validadeAte?: string | null } | null;
}

interface DocumentChecklistProps {
  itens: ItemDoChecklist[];
  title?: string;
  readOnly?: boolean;
}

const CONFIGURACAO_POR_SITUACAO: Record<
  SituacaoDoChecklist,
  { icon: LucideIcon; cor: string; rotulo: string }
> = {
  aprovado: { icon: Check, cor: 'text-success', rotulo: 'Aprovado' },
  enviado: { icon: Clock, cor: 'text-warning', rotulo: 'Enviado' },
  pendente: { icon: FileText, cor: 'text-muted-foreground', rotulo: 'Pendente' },
  rejeitado: { icon: X, cor: 'text-destructive', rotulo: 'Rejeitado' },
};

/**
 * Checklist de documentos de uma pessoa/veículo/credenciado.
 *
 * Versão simplificada do protótipo (`components/DocumentChecklist.tsx`, 425
 * linhas): lá o componente buscava requisito, documento, natureza do
 * credenciado e status de OCR direto do Supabase, com upload real, reuso de
 * documento entre solicitações e aprovação inline. Nada disso existe neste
 * repositório — não há módulo de requisitos documentais, armazenamento de
 * arquivo, nem fila de OCR (`apps/api/src/modulos/` não tem nenhum dos três).
 *
 * Por isso este componente é **apresentacional**: recebe os itens prontos
 * (mock de leitura, TEMPORÁRIO — CLAUDE.md, "Proibido", exceção de
 * 2026-08-27) e nenhum botão grava nada de verdade. "Enviar" mostra o mesmo
 * aviso honesto que o resto do sistema usa para escrita indisponível
 * (`cadastroIndisponivel.ts`) — nunca abre um seletor de arquivo de verdade,
 * porque fingir que o arquivo seria processado é pior que não ter o botão.
 */
export function DocumentChecklist({
  itens,
  title = 'Documentos Necessários',
  readOnly,
}: DocumentChecklistProps) {
  const total = itens.length;
  const enviados = itens.filter((item) => item.status !== 'pendente').length;
  const progresso = total > 0 ? Math.round((enviados / total) * 100) : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <span className="text-muted-foreground text-xs">
            {enviados}/{total} documentos
          </span>
        </div>
        <Progress value={progresso} className="h-2" />
      </CardHeader>
      <CardContent className="space-y-2">
        {itens.map((item) => {
          const config = CONFIGURACAO_POR_SITUACAO[item.status];
          const Icon = config.icon;
          const podeEnviar =
            !readOnly && (item.status === 'pendente' || item.status === 'rejeitado');

          return (
            <div
              key={item.id}
              className={cn(
                'flex items-center gap-3 rounded-lg border p-3',
                item.status === 'pendente' && item.obrigatorio && 'border-warning/30 bg-warning/5',
                item.status === 'aprovado' && 'border-success/30 bg-success/5',
                item.status === 'rejeitado' && 'border-destructive/30 bg-destructive/5',
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  item.status === 'pendente' ? 'bg-muted' : 'bg-background',
                )}
              >
                <Icon className={cn('h-4 w-4', config.cor)} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">
                  {item.nomeDocumento}
                  {item.obrigatorio && <span className="text-destructive ml-1">*</span>}
                </p>
                {item.descricao && (
                  <p className="text-muted-foreground truncate text-xs">{item.descricao}</p>
                )}
                {item.documento && (
                  <p className="text-primary mt-0.5 flex items-center gap-1 text-xs">
                    <FileText className="h-3 w-3" aria-hidden="true" />
                    {item.documento.arquivoNome}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {item.documento?.validadeAte && (
                  <DocValidadeBadge
                    validade={item.documento.validadeAte}
                    hideWhenEmpty
                    className="text-[10px]"
                  />
                )}
                <span className={cn('text-xs font-medium', config.cor)}>{config.rotulo}</span>
                {podeEnviar && (
                  <Button
                    size="sm"
                    variant="default"
                    className="h-8 gap-1"
                    onClick={() => mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL)}
                  >
                    <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                    Enviar
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {!readOnly && enviados < total && (
          <p className="text-warning mt-2 flex items-center gap-1 text-xs">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Envie todos os documentos obrigatórios para manter o cadastro regularizado.
          </p>
        )}
        {itens.length === 0 && (
          <p className="text-muted-foreground py-6 text-center text-sm">
            Nenhum documento obrigatório cadastrado para este tipo.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
